import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { postDataAPI } from '../../utils/fetchData';

const FeedbackForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const initialEmailSet = useRef(false);

  // Check if user is logged in
  const token = localStorage.getItem('dizeeToken');
  const userDataString = localStorage.getItem('dizeeUser');
  const userData = userDataString ? JSON.parse(userDataString) : {};
  const isLoggedIn = !!token;
  
    // Determine if we should skip email question  
  const shouldSkipEmail = isLoggedIn && userData.email;

  // Prevent scroll like other Dizee apps
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, []);

 
  
  const [formData, setFormData] = useState({
    email: '',
    overallRating: '',
    descriptionOfDizee: '',
    
    dataIntegrationSuccess: '',
    dataIntegrationMissing: '',
    
    usedAiSearch: '',
    aiSearchFindings: '',
    presetPrompts: '',
    
    dailyPainPoints: '',
    
    usefulMiniApps: [],
    wishListTool: '',
    
    easeOfUse: '',
    
    missingFeatures: '',
    bugsEncountered: '',
    
    magicWandFeature: '',
    finalThoughts: '',
    usageFrequency: ''
  });

  // Set initial email for logged-in users
  useEffect(() => {
    if (shouldSkipEmail && userData.email && !initialEmailSet.current) {
      setFormData(prev => ({
        ...prev,
        email: userData.email
      }));
      initialEmailSet.current = true;
    }
  }, [shouldSkipEmail, userData.email]);

  const allQuestions = [
    {
      id: 'intro',
      title: 'DIZEE Beta Feedback Questionnaire',
      subtitle: "Thanks for testing Dizee. We're building this for you — your feedback helps shape what it becomes.",
      type: 'intro'
    },
    {
      id: 'email',
      title: 'Contact Information *',
      subtitle: 'What\'s your email address?',
      placeholder: 'your@email.com',
      type: 'input'
    },
    {
      id: 'overallRating',
      title: 'Overall Experience',
      subtitle: 'How would you rate your overall experience using Dizee so far?',
      type: 'radio',
      options: [
        { value: '1', label: '1 - Rough' },
        { value: '2', label: '2 - Needs work' },
        { value: '3', label: '3 - Not bad' },
        { value: '4', label: '4 - Very Useful' },
        { value: '5', label: '5 - I would use this everyday' }
      ]
    },
    {
      id: 'descriptionOfDizee',
      title: 'Overall Experience',
      subtitle: 'In your own words, how would you describe what Dizee is to someone else in the industry?',
      placeholder: 'Example: "It\'s like if Chartmetric, your booking agent, and your AI assistant had a baby."',
      type: 'textarea'
    },
    {
      id: 'dataIntegrationSuccess',
      title: 'Data Integration',
      subtitle: 'Did Dizee successfully connect and pull in the platforms you use (Spotify, Apple, Instagram, TikTok, label portals, etc.)?',
      type: 'radio',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'mostly', label: 'Mostly' },
        { value: 'not-really', label: 'Not really' },
        { value: 'not-at-all', label: 'Not at all' }
      ]
    },
    {
      id: 'dataIntegrationMissing',
      title: 'Data Integration',
      subtitle: 'What was missing or didn\'t connect properly?',
      placeholder: 'Example: "Would love to pull in Beatport data and EPK links."',
      type: 'textarea'
    },
    {
      id: 'usedAiSearch',
      title: 'AI Search + Prompts',
      subtitle: 'Have you used the AI search bar to ask questions about your career?',
      type: 'radio',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'tried-once-twice', label: 'Tried it once or twice' },
        { value: 'not-yet', label: 'Not yet' }
      ]
    },
    {
      id: 'aiSearchFindings',
      title: 'AI Search + Prompts',
      subtitle: 'If yes, what\'s something useful or surprising it told you?',
      placeholder: 'Example: "I asked which city I stream the most in, and it helped me target ads for a show."',
      type: 'textarea'
    },
    {
      id: 'presetPrompts',
      title: 'AI Search + Prompts',
      subtitle: 'What pre set prompts would you like to see added in dizee?',
      placeholder: 'Example: "Suggest collab partners based on my sound + following."\nExample: "Which of my songs is most likely to land a sync placement?"',
      type: 'textarea'
    },
    {
      id: 'dailyPainPoints',
      title: 'Daily Pain Points',
      subtitle: 'What are some day-to-day tasks in your career that feel repetitive, frustrating, or time-consuming?',
      placeholder: 'Example: "Manually checking Spotify for Artists and then copy/pasting data to my manager."\nExample: "Following up with my agent about ticket counts — every week."',
      type: 'textarea'
    },
    {
      id: 'usefulMiniApps',
      title: 'Mini Apps + Tools',
      subtitle: 'Which mini apps or tools did you find most useful? (Check all that apply)',
      type: 'checkbox',
      options: [
        { value: 'tour-bookings', label: 'Tour Bookings' },
        { value: 'merch-manager', label: 'Merch Manager' },
        { value: 'playlisting', label: 'Playlisting' },
        { value: 'charting', label: 'Charting' },
        { value: 'social-listening', label: 'Social Listening' },
        { value: 'analytics', label: 'Analytics' },
        { value: 'spotify-rev', label: 'Spotify Rev' },
        { value: 'not-explored', label: 'Haven\'t explored much yet' },
        { value: 'other', label: 'Other' }
      ]
    },
    {
      id: 'wishListTool',
      title: 'Mini Apps + Tools',
      subtitle: 'What\'s a tool or mini-app you wish existed inside Dizee but doesn\'t yet?',
      placeholder: 'Example: "An AI-powered sync tracker to manage pitches and placements."',
      type: 'textarea'
    },
    {
      id: 'easeOfUse',
      title: 'Ease of Use',
      subtitle: 'How easy was it to get up and running?',
      type: 'radio',
      options: [
        { value: 'super-intuitive', label: 'Super intuitive' },
        { value: 'pretty-easy', label: 'Pretty easy' },
        { value: 'took-bit-to-figure', label: 'Took a bit to figure out' },
        { value: 'confusing-at-times', label: 'Confusing at times' },
        { value: 'still-figuring-out', label: 'Still figuring it out' }
      ]
    },
    {
      id: 'missingFeatures',
      title: 'Missing Features or Bugs',
      subtitle: 'What\'s something you expected to see in Dizee but didn\'t?',
      placeholder: 'Example: "More radio analytics or charting outside Spotify and Apple."',
      type: 'textarea'
    },
    {
      id: 'bugsEncountered',
      title: 'Missing Features or Bugs',
      subtitle: 'Did you run into any bugs, glitches, or broken features?',
      placeholder: 'Example: "When I clicked into merch, it didn\'t pull from my Shopify correctly."',
      type: 'textarea'
    },
    {
      id: 'magicWandFeature',
      title: 'Final Thoughts',
      subtitle: 'If you could wave a magic wand and instantly add one thing to Dizee, what would it be?',
      placeholder: 'Example: "A smart calendar that auto-schedules content based on tour dates + release schedule."',
      type: 'textarea'
    },
    {
      id: 'usageFrequency',
      title: 'Usage Frequency',
      subtitle: 'I would use dizee:',
      type: 'radio',
      options: [
        { value: 'every-hour', label: 'Every hour like social media' },
        { value: 'few-times-day', label: 'A few times a day' },
        { value: 'few-times-week', label: 'A few times a week' },
        { value: 'few-times-month', label: 'A few times a month' }
      ]
    },
    {
      id: 'finalThoughts',
      title: 'Final Thoughts',
      subtitle: 'Anything else you want to tell us — good, bad, random?',
      placeholder: 'Example: "Honestly, the UI feels clean but I\'d love a dark mode option too."',
      type: 'textarea'
    }
  ];

  // Filter out email question for logged-in users who have email in their data
  const questions = shouldSkipEmail ? allQuestions.filter(q => q.id !== 'email') : allQuestions;
  
  const currentQuestion = questions[currentStep];

  const handleInputChange = (value, fieldId = null) => {
    const targetField = fieldId || currentQuestion.id;
    
    if (fieldId) {
      // Handle comment fields
      setFormData(prev => ({
        ...prev,
        [targetField]: value
      }));
    } else if (currentQuestion.type === 'checkbox') {
      const currentValues = formData[currentQuestion.id] || [];
      if (currentValues.includes(value)) {
        setFormData(prev => ({
          ...prev,
          [currentQuestion.id]: currentValues.filter(v => v !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [currentQuestion.id]: [...currentValues, value]
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [currentQuestion.id]: value
      }));
    }
  };

  const nextStep = () => {
    // Validate email field if on email step (only for non-logged in users)
    if (currentQuestion.id === 'email' && !formData.email?.trim()) {
      // Just return without showing error for now
      return;
    }
    
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipStep = () => {
    nextStep();
  };

  const handleSubmit = async () => {
    // Validate email is provided
    if (!formData.email?.trim()) {
      // Just return without submitting if no email
      return;
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('dizeeToken');
      
      // Try authenticated submission first, then fall back to public submission
      try {
        let response;
        if (token) {
          response = await postDataAPI('feedback/detailed-submit', formData, token);
        } else {
          response = await postDataAPI('feedback/public-detailed-submit', formData);
        }
        
        if (response?.data?.status === 200) {
          setShowSuccess(true);
          
          // Reset form immediately (preserve email for logged-in users)
          setFormData({
            email: shouldSkipEmail ? userData.email : '',
            overallRating: '',
            descriptionOfDizee: '',
            dataIntegrationSuccess: '',
            dataIntegrationMissing: '',
            usedAiSearch: '',
            aiSearchFindings: '',
            presetPrompts: '',
            dailyPainPoints: '',
            usefulMiniApps: [],
            wishListTool: '',
            easeOfUse: '',
            missingFeatures: '',
            bugsEncountered: '',
            magicWandFeature: '',
            usageFrequency: '',
            finalThoughts: ''
          });
          
          // Reset to beginning after delay
          setTimeout(() => {
            setCurrentStep(0);
            setShowSuccess(false);
          }, 3000);
        }
              } catch (authError) {
          // If authenticated request fails, try public endpoint
          if (token) {
            const response = await postDataAPI('feedback/public-detailed-submit', formData);
            if (response?.data?.status === 200) {
              setShowSuccess(true);
              
              // Reset form immediately (preserve email for logged-in users)
              setFormData({
                email: shouldSkipEmail ? userData.email : '',
                overallRating: '',
                descriptionOfDizee: '',
                dataIntegrationSuccess: '',
                dataIntegrationMissing: '',
                usedAiSearch: '',
                aiSearchFindings: '',
                presetPrompts: '',
                dailyPainPoints: '',
                usefulMiniApps: [],
                wishListTool: '',
                easeOfUse: '',
                missingFeatures: '',
                bugsEncountered: '',
                magicWandFeature: '',
                usageFrequency: '',
                finalThoughts: ''
              });
              
              // Reset to beginning after delay
              setTimeout(() => {
                setCurrentStep(0);
                setShowSuccess(false);
              }, 3000);
            }
          } else {
            throw authError;
          }
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      // Handle error without global alert system
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = () => {
    const question = currentQuestion;

    // Show success message
    if (showSuccess) {
      return (
        <div style={{ textAlign: 'left', padding: '0 5%' }}>
          <div style={{ fontSize: '16px', fontWeight: '500', color: 'white', marginBottom: '20px' }}>
            Thank you!
          </div>
          <div style={{ fontSize: '12px', color: 'white', opacity: '0.9', marginBottom: '40px', lineHeight: '1.5' }}>
            Your detailed feedback has been submitted successfully.
          </div>
        </div>
      );
    }

    if (question.type === 'intro') {
      return (
        <div style={{ textAlign: 'left', padding: '0 5%' }}>
          <div style={{ fontSize: '16px', fontWeight: '500', color: 'white', marginBottom: '20px' }}>
            {question.title}
          </div>
          <div style={{ fontSize: '12px', color: 'white', opacity: '0.9', marginBottom: '40px', lineHeight: '1.5' }}>
            {question.subtitle}
          </div>
          <div style={{ width: '40px', height: '1px', backgroundColor: 'white', opacity: '0.5', marginBottom: '40px' }}></div>
          <button 
            onClick={nextStep}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'white', 
              fontSize: '12px', 
              opacity: '0.7', 
              cursor: 'pointer' 
            }}
          >
            Start Survey
          </button>
        </div>
      );
    }

    return (
      <div style={{ padding: '0 5%', display: 'flex', flexDirection: 'column' }}>
        {/* Progress indicator */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ fontSize: '12px', color: 'white', opacity: '0.6', marginBottom: '10px' }}>
            {currentStep} of {questions.length - 1}
          </div>
          <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', marginBottom: '20px' }}>
            <div 
              style={{ 
                backgroundColor: 'white', 
                height: '100%', 
                width: `${(currentStep / (questions.length - 1)) * 100}%`,
                transition: 'width 0.3s ease'
              }}
            ></div>
          </div>
        </div>

        {/* Question content */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '16px', fontWeight: '500', color: 'white', marginBottom: '15px', textAlign: 'left' }}>
            {question.title}
          </div>
          <div style={{ fontSize: '12px', color: 'white', opacity: '0.9', marginBottom: '30px', lineHeight: '1.5', textAlign: 'left' }}>
            {question.subtitle}
          </div>

          {/* Input area */}
          <div style={{ marginBottom: '30px' }}>
            {question.type === 'radio' && (
              <div>
                {question.options.map((option) => (
                  <label 
                    key={option.value}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer', 
                      marginBottom: '15px',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{ position: 'relative', marginRight: '12px' }}>
                      <input
                        type="radio"
                        name={question.id}
                        value={option.value}
                        checked={formData[question.id] === option.value}
                        onChange={(e) => handleInputChange(e.target.value)}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '1px solid rgba(255,255,255,0.5)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderColor: formData[question.id] === option.value ? 'white' : 'rgba(255,255,255,0.5)'
                      }}>
                        {formData[question.id] === option.value && (
                          <div style={{ width: '8px', height: '8px', backgroundColor: 'white', borderRadius: '50%' }}></div>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: '12px', color: 'white', opacity: '0.9' }}>
                      {option.label}
                    </span>
                  </label>
                ))}
                
                {/* Comment box for radio questions */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ fontSize: '12px', color: 'white', opacity: '0.7', marginBottom: '8px' }}>
                    Additional comments (optional):
                  </div>
                  <input
                    type="text"
                    value={formData[`${question.id}_comment`] || ''}
                    onChange={(e) => handleInputChange(e.target.value, `${question.id}_comment`)}
                    placeholder="Any other thoughts..."
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '12px',
                      padding: '8px',
                      fontFamily: 'Helvetica',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            )}

            {question.type === 'checkbox' && (
              <div>
                {question.options.map((option) => (
                  <label 
                    key={option.value}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer', 
                      marginBottom: '15px',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{ position: 'relative', marginRight: '12px' }}>
                      <input
                        type="checkbox"
                        value={option.value}
                        checked={(formData[question.id] || []).includes(option.value)}
                        onChange={(e) => handleInputChange(e.target.value)}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '1px solid rgba(255,255,255,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderColor: (formData[question.id] || []).includes(option.value) ? 'white' : 'rgba(255,255,255,0.5)',
                        backgroundColor: (formData[question.id] || []).includes(option.value) ? 'white' : 'transparent'
                      }}>
                        {(formData[question.id] || []).includes(option.value) && (
                          <svg style={{ width: '10px', height: '10px', fill: 'black' }} viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: '12px', color: 'white', opacity: '0.9' }}>
                      {option.label}
                    </span>
                  </label>
                ))}
                
                {/* Comment box for checkbox questions */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ fontSize: '12px', color: 'white', opacity: '0.7', marginBottom: '8px' }}>
                    Additional comments (optional):
                  </div>
                  <input
                    type="text"
                    value={formData[`${question.id}_comment`] || ''}
                    onChange={(e) => handleInputChange(e.target.value, `${question.id}_comment`)}
                    placeholder="Any other thoughts..."
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '12px',
                      padding: '8px',
                      fontFamily: 'Helvetica',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            )}

            {question.type === 'textarea' && (
              <textarea
                value={formData[question.id] || ''}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                style={{
                  width: '100%',
                  height: '120px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  fontSize: '12px',
                  padding: '12px',
                  fontFamily: 'Helvetica',
                  resize: 'none',
                  outline: 'none'
                }}
              />
            )}

            {question.type === 'input' && (
              <input
                type="text"
                value={formData[question.id] || ''}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  fontSize: '12px',
                  padding: '12px',
                  fontFamily: 'Helvetica',
                  outline: 'none'
                }}
              />
            )}
          </div>

          {/* Navigation buttons - positioned right after input area */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
            <div style={{ display: 'flex', gap: '20px' }}>
              {currentStep > 0 && (
                <button 
                  onClick={prevStep}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: 'white', 
                    fontSize: '12px', 
                    opacity: '0.7', 
                    cursor: 'pointer' 
                  }}
                >
                  Back
                </button>
              )}
              {/* Hide skip button on email page */}
              {currentQuestion.id !== 'email' && (
                <button 
                  onClick={skipStep}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: 'white', 
                    fontSize: '12px', 
                    opacity: '0.7', 
                    cursor: 'pointer' 
                  }}
                >
                  Skip
                </button>
              )}
            </div>

            {currentStep === questions.length - 1 ? (
              <button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'white', 
                  fontSize: '12px', 
                  opacity: isSubmitting ? '0.5' : '0.7', 
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            ) : (
              <button 
                onClick={nextStep}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'white', 
                  fontSize: '12px', 
                  opacity: '0.7', 
                  cursor: 'pointer' 
                }}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh', 
      width: '100%', 
      margin: '0 auto', 
      overflow: 'hidden', 
      position: 'relative', 
      backgroundColor: 'black',
      fontFamily: 'Helvetica'
    }}>
              {/* Main Content */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          maxWidth: '390px',
          margin: '0 auto',
          width: '100%',
          paddingTop: '5%',
          paddingBottom: '5%',
          justifyContent: 'flex-start'
        }}>
          {renderQuestion()}
        </div>
    </div>
  );
};

export default FeedbackForm; 